import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import type { EntitlementFeature, ScopeEntitlement } from '@app/src/types/entitlements'
import type { SolutionType } from '@app/src/types/resourceExplorer'
import { CircularProgress, Link, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import { getSolutionTypeLabel } from '../availabilityHelpers'
import { ruleDescription } from './templatePolicy'

type Props = {
  feature: EntitlementFeature
  scopeId: number
}

const OrganizationRules: React.FC<Props> = ({ feature, scopeId }) => {
  const { formatMessage } = useIntl()
  const { closeDrawer } = useDrawer()
  const {
    data: rules = [],
    isLoading,
    isError,
  } = useFetchResource<ScopeEntitlement[]>({
    key: [FetchKey.ScopeEntitlements, feature, scopeId],
    endpoint: endpoints.scopeEntitlements(feature, scopeId),
  })

  const solutionLabel = (solutionType: SolutionType) => getSolutionTypeLabel(solutionType, formatMessage)

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Organizations with their own rule
      </Typography>

      {isLoading && <CircularProgress size={16} />}

      {isError && (
        <Typography variant="body2" color="error">
          Couldn&apos;t load the organizations with their own rule.
        </Typography>
      )}

      {!isLoading && !isError && rules.length === 0 && <Typography variant="body2">None</Typography>}

      {rules.map(rule => (
        <Stack
          key={`${rule.organizationId}-${rule.solutionType}`}
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          columnGap={2}
          flexWrap="wrap"
        >
          <Link
            component={RouterLink}
            to={`/configurations/organizations/${rule.organizationId}?tab=access`}
            variant="body2"
            onClick={closeDrawer}
          >
            {rule.organizationName}
          </Link>
          <Typography variant="caption" color="text.secondary">
            {ruleDescription(rule, solutionLabel)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export default OrganizationRules
